import { useState, useCallback, useEffect, useRef } from "react";

interface UseCarouselOptions {
  itemCount: number;
  autoRotate?: boolean;
  autoRotateInterval?: number;
  transitionDuration?: number;
}

interface UseCarouselReturn {
  currentIndex: number;
  isTransitioning: boolean;
  goToSlide: (index: number) => void;
  goToNext: () => void;
  goToPrevious: () => void;
  resetAutoRotate: () => void;
}

/**
 * Custom hook for carousel functionality with auto-rotation
 */
export const useCarousel = ({
  itemCount,
  autoRotate = false,
  autoRotateInterval = 7000,
  transitionDuration = 500,
}: UseCarouselOptions): UseCarouselReturn => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoRotateTimerRef = useRef<number | null>(null);
  const swapTimerRef = useRef<number | null>(null);
  const completeTimerRef = useRef<number | null>(null);

  const goToSlide = useCallback(
    (index: number) => {
      if (isTransitioning || index === currentIndex || index < 0 || index >= itemCount) return;

      // Clear any existing transition timers
      if (swapTimerRef.current) {
        clearTimeout(swapTimerRef.current);
        swapTimerRef.current = null;
      }
      if (completeTimerRef.current) {
        clearTimeout(completeTimerRef.current);
        completeTimerRef.current = null;
      }

      // Start fade out
      setIsTransitioning(true);

      // Swap image at half transition (when opacity is 0)
      swapTimerRef.current = window.setTimeout(() => {
        setCurrentIndex(index);
        swapTimerRef.current = null;
      }, transitionDuration / 2);

      // Complete transition (fade in)
      completeTimerRef.current = window.setTimeout(() => {
        setIsTransitioning(false);
        completeTimerRef.current = null;
      }, transitionDuration);
    },
    [currentIndex, isTransitioning, itemCount, transitionDuration],
  );

  const goToNext = useCallback(() => {
    if (itemCount === 0) return;
    const nextIndex = (currentIndex + 1) % itemCount;
    goToSlide(nextIndex);
  }, [currentIndex, itemCount, goToSlide]);

  const goToPrevious = useCallback(() => {
    if (itemCount === 0) return;
    const previousIndex = (currentIndex - 1 + itemCount) % itemCount;
    goToSlide(previousIndex);
  }, [currentIndex, itemCount, goToSlide]);

  const resetAutoRotate = useCallback(() => {
    if (autoRotateTimerRef.current) {
      clearInterval(autoRotateTimerRef.current);
    }
    if (autoRotate && itemCount > 1) {
      autoRotateTimerRef.current = window.setInterval(goToNext, autoRotateInterval);
    }
  }, [autoRotate, itemCount, goToNext, autoRotateInterval]);

  // Auto-rotate effect
  useEffect(() => {
    if (!autoRotate || itemCount <= 1) return;

    autoRotateTimerRef.current = window.setInterval(goToNext, autoRotateInterval);

    return () => {
      if (autoRotateTimerRef.current) {
        clearInterval(autoRotateTimerRef.current);
      }
    };
  }, [autoRotate, itemCount, goToNext, autoRotateInterval]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      if (swapTimerRef.current) {
        clearTimeout(swapTimerRef.current);
      }
      if (completeTimerRef.current) {
        clearTimeout(completeTimerRef.current);
      }
      if (autoRotateTimerRef.current) {
        clearInterval(autoRotateTimerRef.current);
      }
    };
  }, []);

  return {
    currentIndex,
    isTransitioning,
    goToSlide,
    goToNext,
    goToPrevious,
    resetAutoRotate,
  };
};
